export const normalizePartnerSlug = (value) =>
  (value || '').toString().trim().toLowerCase();

export const getPartnerFleetId = (partner) => {
  if (!partner) return null;
  return (
    partner.fleetadmin ||
    partner.fleetadminId ||
    partner.fleetAdmin ||
    partner.fleetAdminId ||
    partner.fleetAdminUID ||
    partner.uid ||
    partner.userId ||
    null
  );
};

export const getPartnerDisplayName = (partner, settings) => {
  if (!partner) return settings && settings.CompanyName ? settings.CompanyName : '';
  return (
    partner.brandName ||
    partner.brand ||
    partner.name ||
    partner.companyName ||
    partner.businessName ||
    partner.title ||
    partner.company ||
    partner.displayName ||
    (settings && settings.CompanyName ? settings.CompanyName : '')
  );
};

export const isPartnerMaintenance = (partner) => {
  if (!partner) return false;
  return Boolean(partner.maintenanceMode || partner.maintenance || partner.maintenance_mode);
};

export const getPartnerMaintenanceMessage = (partner) =>
  partner?.maintenanceMessage || partner?.maintenance_message || '';

export const isPartnerComingSoon = (partner) => {
  if (!partner) return false;
  return Boolean(partner.comingSoon || partner.comingSoonMode || partner.coming_soon);
};

export const getPartnerComingSoonMessage = (partner) =>
  partner?.comingSoonMessage || partner?.coming_soon_message || '';

const getPartnerSlugValue = (partner) =>
  partner &&
  (partner.slug ||
    partner.vanitySlug ||
    partner.publicSlug ||
    partner.customSlug ||
    partner.path ||
    partner.url ||
    partner.partnerSlug ||
    partner.partner_url ||
    partner.partner);

const asPartnerList = (partnerPages) => {
  if (!partnerPages) return [];
  if (Array.isArray(partnerPages)) return partnerPages;
  if (typeof partnerPages === 'object') return Object.values(partnerPages);
  return [];
};

const normalizeHost = (host) => {
  if (!host) return '';
  return host.toString().trim().toLowerCase().split(':')[0];
};

const isLocalHost = (host) => {
  if (!host) return true;
  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
};

const getHostFromUrl = (value) => {
  if (!value) return null;
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return normalizeHost(url.hostname);
  } catch (e) {
    return normalizeHost(value);
  }
};

const getBaseHost = (settings) => {
  const companyWebsite = settings?.CompanyWebsite || settings?.companyWebsite;
  return companyWebsite ? getHostFromUrl(companyWebsite) : null;
};

const getSubdomain = (hostname, baseHost) => {
  if (!hostname) return null;
  if (baseHost && hostname.endsWith(baseHost)) {
    const remainder = hostname.slice(0, -baseHost.length).replace(/\.$/, '');
    if (!remainder) return null;
    return remainder.split('.')[0];
  }
  const parts = hostname.split('.');
  if (parts.length < 3) return null;
  if (hostname.endsWith('web.app')) return null;
  return parts[0];
};

const hostMatchesPartner = (partner, hostname) => {
  if (!partner || !hostname) return false;
  const domains = partner.domains || partner.domain || partner.host || partner.hostname;
  if (!domains) return false;
  const list = Array.isArray(domains) ? domains : [domains];
  return list.some((domain) => getHostFromUrl(domain) === hostname);
};

const getDomainMode = (partner) => {
  const raw =
    partner?.domainMode ||
    partner?.domain_mode ||
    partner?.domainType ||
    partner?.domain_type ||
    '';
  const mode = raw.toString().trim().toLowerCase();
  if (mode === 'standalone' || mode === 'custom' || mode === 'domain') return 'standalone';
  if (mode === 'subdomain') return 'subdomain';
  if (mode === 'both') return 'both';
  return 'both';
};

export const resolvePartnerConfig = (settings, slug) => {
  if (!settings || !slug) return null;
  const normalized = normalizePartnerSlug(slug);
  const partnerPages =
    settings.partnerPages ||
    settings.partners ||
    settings.partner_pages ||
    settings.partnerPagesConfig;

  if (!partnerPages) return null;

  if (!Array.isArray(partnerPages) && typeof partnerPages === 'object') {
    const direct = partnerPages[normalized] || partnerPages[slug];
    if (direct) {
      if (direct.active === false || direct.subscriptionStatus === 'inactive') {
        return { ...direct, inactive: true };
      }
      return direct;
    }
  }

  const list = asPartnerList(partnerPages);
  const match = list.find(
    (partner) => normalizePartnerSlug(getPartnerSlugValue(partner)) === normalized
  );

  if (!match) return null;
  if (match.active === false || match.subscriptionStatus === 'inactive') {
    return { ...match, inactive: true };
  }
  return match;
};

export const resolvePartnerFromHost = (settings, host) => {
  if (!settings) return null;
  const hostname = normalizeHost(host);
  if (!hostname || isLocalHost(hostname)) return null;

  const partnerPages =
    settings.partnerPages ||
    settings.partners ||
    settings.partner_pages ||
    settings.partnerPagesConfig;

  if (!partnerPages) return null;
  const list = asPartnerList(partnerPages);

  const directMatch = list.find((partner) => {
    const mode = getDomainMode(partner);
    if (mode === 'subdomain') return false;
    return hostMatchesPartner(partner, hostname);
  });
  if (directMatch) {
    if (directMatch.active === false || directMatch.subscriptionStatus === 'inactive') {
      return { ...directMatch, inactive: true };
    }
    return directMatch;
  }

  const baseHost = getBaseHost(settings);
  const subdomain = getSubdomain(hostname, baseHost);
  if (!subdomain) return null;
  const normalizedSub = normalizePartnerSlug(subdomain);

  const subMatch = list.find((partner) => {
    const mode = getDomainMode(partner);
    if (mode === 'standalone') return false;
    const configuredSub = normalizePartnerSlug(partner?.subdomain || '');
    if (configuredSub && configuredSub === normalizedSub) return true;
    const slugValue = normalizePartnerSlug(getPartnerSlugValue(partner));
    return slugValue && slugValue === normalizedSub;
  });

  if (!subMatch) return null;
  if (subMatch.active === false || subMatch.subscriptionStatus === 'inactive') {
    return { ...subMatch, inactive: true };
  }
  return subMatch;
};

const DEFAULT_WIDGET_PRESETS = {
  default: ['hero', 'stats', 'fleet', 'coverage', 'pricing', 'testimonials', 'safety', 'cta', 'apps'],
  corporate: ['hero', 'stats', 'coverage', 'pricing', 'safety', 'cta', 'apps'],
  luxury: ['hero', 'fleet', 'testimonials', 'pricing', 'cta'],
  eco: ['hero', 'stats', 'coverage', 'safety', 'cta', 'apps'],
};

const DEFAULT_THEME = {
  primary: '#111827',
  secondary: '#0f172a',
  accent: '#f59e0b',
  background: '#f8fafc',
  surface: '#ffffff',
  text: '#0b0b0b',
  muted: '#6b7280',
  heroFrom: '#111827',
  heroTo: '#0f172a',
  fontHeading: '"Space Grotesk", "Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontBody: '"Manrope", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

export const getPartnerTheme = (partner = {}) => {
  const theme = partner.theme || {};
  return {
    primary: theme.primary || theme.Header || DEFAULT_THEME.primary,
    secondary: theme.secondary || DEFAULT_THEME.secondary,
    accent: theme.accent || DEFAULT_THEME.accent,
    background: theme.background || theme.LandingPage_Background || DEFAULT_THEME.background,
    surface: theme.surface || DEFAULT_THEME.surface,
    text: theme.text || theme.Header_Text || DEFAULT_THEME.text,
    muted: theme.muted || DEFAULT_THEME.muted,
    heroFrom: theme.heroFrom || (Array.isArray(theme.heroGradient) ? theme.heroGradient[0] : null) || DEFAULT_THEME.heroFrom,
    heroTo: theme.heroTo || (Array.isArray(theme.heroGradient) ? theme.heroGradient[1] : null) || DEFAULT_THEME.heroTo,
    fontHeading: theme.fontHeading || DEFAULT_THEME.fontHeading,
    fontBody: theme.fontBody || DEFAULT_THEME.fontBody,
  };
};

const buildWidget = (type, partner, settings) => {
  const partnerName = getPartnerDisplayName(partner, settings) || 'Partner';
  const defaultWidget = {
    type,
    enabled: true,
    data: {},
  };

  const widgetData = {
    hero: {
      eyebrow: partner.tagline || 'Premium partner',
      title: partner.heroTitle || `${partnerName} - premium rides, local expertise`,
      subtitle:
        partner.description ||
        'Fast pickups, transparent pricing, and professional drivers - tailored to your city.',
      ctaPrimary: partner.ctaLabel || 'Book with us',
      ctaSecondary: 'Talk to sales',
      image: partner.heroImage || partner.bannerImage || partner.logo,
      badges: ['24/7 Dispatch', 'Airport Priority', 'Business Accounts'],
    },
    stats: {
      title: 'Trusted at scale',
      subtitle: 'Operational excellence backed by real-time analytics.',
      items: [
        { label: 'Avg pickup', value: '3.2 min' },
        { label: 'Fleet size', value: '120+' },
        { label: 'Rating', value: '4.9 stars' },
        { label: 'Monthly rides', value: '18K' },
      ],
    },
    fleet: {
      title: 'Fleet spotlight',
      subtitle: 'Vehicles built for comfort, compliance, and business travel.',
      items: [
        { name: 'Executive Sedan', seats: '1-4', features: ['Leather interior', 'Wi-Fi', 'Meet & Greet'] },
        { name: 'Premium Van', seats: '1-6', features: ['Extra luggage', 'Quiet cabin', 'USB-C'] },
        { name: 'Eco EV', seats: '1-4', features: ['Zero emissions', 'Low noise', 'Priority lanes'] },
      ],
    },
    coverage: {
      title: 'Coverage & service zones',
      subtitle: 'Strategic coverage in key business districts and airports.',
      areas: ['Downtown Core', 'Financial District', 'Airport & Hotels', 'Tech Campus', 'University'],
      note: "Need a custom zone? We'll configure it in minutes.",
    },
    pricing: {
      title: 'Subscription plans',
      subtitle: 'Select a plan that matches your dispatch volume.',
      plans: [
        { name: 'Starter', price: '$299/mo', highlight: false, features: ['Up to 500 rides', 'Email support', 'Branding'] },
        { name: 'Growth', price: '$799/mo', highlight: true, features: ['Up to 2,000 rides', 'Priority support', 'Dedicated success'] },
        { name: 'Enterprise', price: 'Custom', highlight: false, features: ['Unlimited rides', 'SLA & compliance', 'Custom integrations'] },
      ],
    },
    testimonials: {
      title: 'What partners say',
      items: [
        { name: 'Lisa M.', role: 'Operations Lead', quote: 'We cut dispatch time by 40% and boosted driver utilization.' },
        { name: 'Jonas B.', role: 'Fleet Owner', quote: 'The branded page helped us win corporate contracts overnight.' },
        { name: 'Nora R.', role: 'Hotel Concierge', quote: 'Reliable rides every time. Guests love the smooth pickup flow.' },
      ],
    },
    safety: {
      title: 'Safety & compliance',
      items: [
        { title: 'Verified drivers', desc: 'Background checks and documentation on every driver.' },
        { title: 'Live trip tracking', desc: 'Monitor rides in real time with automated alerts.' },
        { title: '24/7 support', desc: 'Always-on assistance for riders and dispatchers.' },
      ],
    },
    cta: {
      title: `Launch ${partnerName} on Plukk`,
      subtitle: 'Activate your partner page and start receiving bookings today.',
      ctaLabel: partner.ctaLabel || 'Activate partnership',
    },
    apps: {
      title: 'Give riders a seamless experience',
      subtitle: 'Send them to the app or book from your branded page.',
      badges: ['App Store', 'Google Play'],
    },
  };

  return { ...defaultWidget, data: widgetData[type] || {} };
};

export const getPartnerWidgets = (partner = {}, settings) => {
  if (Array.isArray(partner.widgets) && partner.widgets.length) {
    return partner.widgets;
  }

  const presetKey = partner.widgetPreset || 'default';
  const presetList = DEFAULT_WIDGET_PRESETS[presetKey] || DEFAULT_WIDGET_PRESETS.default;
  let widgets = presetList.map((type) => buildWidget(type, partner, settings));

  if (Array.isArray(partner.enabledWidgets) && partner.enabledWidgets.length) {
    const enabledSet = new Set(partner.enabledWidgets);
    widgets = widgets.filter((widget) => enabledSet.has(widget.type));
  }

  if (partner.widgetFlags && typeof partner.widgetFlags === 'object') {
    widgets = widgets.map((widget) => ({
      ...widget,
      enabled: partner.widgetFlags[widget.type] !== false,
    }));
  }

  if (partner.widgetContent && typeof partner.widgetContent === 'object') {
    widgets = widgets.map((widget) => {
      const override = partner.widgetContent[widget.type];
      if (!override) return widget;
      return {
        ...widget,
        ...override,
        data: { ...widget.data, ...(override.data || {}) },
      };
    });
  }

  return widgets;
};

export const PARTNER_WIDGET_TYPES = DEFAULT_WIDGET_PRESETS.default;
export const PARTNER_WIDGET_PRESETS = DEFAULT_WIDGET_PRESETS;
